import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../client/query-client';
import { EntitlementResponse, SessionResponse, viewerFrom } from './session';
/**
 * Session and entitlement, fetched in PARALLEL on boot.
 *
 * Neither read needs the other's result, so the cost of `entitled` not being on
 * `SessionResponse` is one extra request rather than one extra round-trip. That
 * was the backend's argument for refusing to merge them and it is correct —
 * and it is why we withdrew that ask (round 2 §1).
 *
 * Entitlement stays re-readable on purpose: activation is asynchronous, a paid
 * signup sits in `PENDING_ACTIVATION` until Stripe's webhook lands, and a client
 * holding a boot-time snapshot cannot see it arrive.
 */
export function useSession(http) {
    return useQuery({
        queryKey: queryKeys.session,
        queryFn: () => http.get('/auth/session', { schema: SessionResponse }),
        // The session is the gate. A stale one shows a member the wrong navigation.
        staleTime: 5 * 60_000,
        retry: false,
    });
}
/**
 * `GET /api/entitlement`, not `/subscriptions/mine`.
 *
 * The latter is a billing read that is blind to B2B; this one resolves through
 * the organisation whose seat you hold and is correct for every role.
 */
/**
 * `locale` is a **parameter and part of the key**, not decoration.
 *
 * `planName` used to answer English on every locale; round 14 made it honour
 * the request locale, which turns two things on at once. The header, so a
 * French member reads `Praticien` — and the key, because without it the first
 * language asked for is cached under a 5-minute `staleTime` and served to the
 * other one. A member switching language would keep the plan name they had,
 * on the rail of every page, for five minutes.
 */
export function useEntitlement(http, locale, enabled) {
    return useQuery({
        queryKey: queryKeys.entitlement(locale),
        queryFn: () => http.get('/entitlement', { schema: EntitlementResponse, locale }),
        enabled,
        staleTime: 5 * 60_000,
    });
}
/**
 * The composed viewer.
 *
 * `isLoading` covers both reads deliberately. Painting a paywall while
 * entitlement is still in flight shows a lock to a paying subscriber, which is
 * worse than a skeleton — and `Entitlement` is `unknown` in that window anyway,
 * so `shouldLock` already refuses. This is the second guard, not the only one.
 */
export function useViewer(http, locale) {
    const session = useSession(http);
    const entitlement = useEntitlement(http, locale, session.isSuccess);
    if (!session.data) {
        return { viewer: null, isLoading: session.isPending, isError: session.isError };
    }
    return {
        viewer: viewerFrom(session.data, entitlement.data),
        isLoading: entitlement.isPending,
        isError: session.isError,
    };
}
//# sourceMappingURL=hooks.js.map